import type { Plan, PlanStop } from '@trippath/shared';

import { wallOf } from './clock';

const DAY = 86_400_000;
const ms = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (time: number) => new Date(time).toISOString().slice(0, 10);

/** When a stop's way on leaves, chosen or not yet; none when it is not said. */
export function leavesAt(stop: PlanStop): string | null {
  return stop.out?.departureAt ?? stop.wayTimes?.departureAt ?? null;
}

/** When a stop's way on arrives, chosen or not yet; none when it is not said. */
export function arrivesAt(stop: PlanStop): string | null {
  return stop.out?.arrivalAt ?? stop.wayTimes?.arrivalAt ?? null;
}

/** Days no stop covers: after a stop's last night, before its way on leaves on a later day. */
export interface Gap {
  /** The stop the days come after, and the one its way on goes to. */
  after: PlanStop;
  next: PlanStop;
  /** The first and the last day of it. */
  first: string;
  last: string;
  days: number;
}

/**
 * The trip's unplanned days: where a way on leaves, as said, later than its stop's nights run —
 * a flight to Bangkok on the 26th, Tallinn's nights over on the 23rd — the days between, by the
 * stop they follow. A way not timed leaves the day its next stop starts; a change on the way
 * left the next day leaves none.
 */
export function gapsOf(plan: Plan): Map<string, Gap> {
  const gaps = new Map<string, Gap>();
  plan.stops.forEach((after, index) => {
    const next = plan.stops[index + 1];
    if (!next) return;
    const said = leavesAt(after);
    const leaves = said ? wallOf(said, after.zone).day : next.start;
    const days = Math.round((ms(leaves) - ms(after.end)) / DAY);
    // A change on the way has no nights: a wait there past midnight is the way, not a gap.
    if (days > (after.transfer ? 1 : 0)) {
      gaps.set(after.id, { after, next, first: after.end, last: iso(ms(leaves) - DAY), days });
    }
  });
  return gaps;
}

/** Whether a day is one no stop covers. */
export function unplanned(gaps: Map<string, Gap>, day: string): boolean {
  return [...gaps.values()].some((gap) => gap.first <= day && day <= gap.last);
}
